import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { formatEuros } from "@sundial/core";
import { exportRelevePdf } from "../lib/exportRelevePdf.js";
import { useReportRange, useSettings, useClients } from "../api/queries.js";
import { ClientFilter } from "../components/ClientFilter.js";
import type { MonthRollup, ReportRangeData } from "../api/queries.js";

/* ── Helpers ──────────────────────────────────────────── */

function parisToday(): string {
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function currentParisMonth(): string {
  return parisToday().slice(0, 7); // "YYYY-MM"
}

function trailing12Start(today: string): string {
  const parts = today.split("-");
  return `${parseInt(parts[0]!, 10) - 1}-${parts[1]!}-${parts[2]!}`;
}

type RangePreset = "thismonth" | "ytd" | "trailing12" | "custom";
type IncludeMode = "worked" | "payments" | "both";

/* ── Shared small components ──────────────────────────── */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        font: "600 11px/14px Sora",
        letterSpacing: ".07em",
        textTransform: "uppercase",
        color: "var(--muted2)",
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

function PillBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 38,
        padding: "0 15px",
        borderRadius: 10,
        cursor: "pointer",
        font: "600 13px Sora",
        transition: ".14s",
        background: active ? "var(--primary)" : "var(--card)",
        color: active ? "#fff" : "var(--text)",
        border: active ? "1px solid var(--primary)" : "1px solid var(--border)",
        boxShadow: active ? "0 2px 9px var(--glow)" : "none",
      }}
    >
      {children}
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  height: 40,
  borderRadius: 10,
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  padding: "0 12px",
  fontSize: 14,
  outline: "none",
  fontFamily: "'JetBrains Mono', monospace",
};

/* ── Tile component ──────────────────────────────────── */

/**
 * HT / TTC basis marker.
 *
 * Sits on every money figure on this screen. The two bases were once visually
 * interchangeable, which is how an HT total came to be subtracted from a TTC
 * one; tagging them is what makes that class of bug structurally impossible.
 */
function BasisPill({ basis }: { basis: "HT" | "TTC" }) {
  const shared: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    font: "700 9px/1 'JetBrains Mono', monospace",
    letterSpacing: ".05em",
    padding: "3px 5px",
    borderRadius: 5,
    flex: "none",
  };
  if (basis === "TTC") {
    return <span style={{ ...shared, color: "#fff", background: "var(--sick, #4E77AE)" }}>TTC</span>;
  }
  return (
    <span
      style={{
        ...shared,
        border: "1px solid color-mix(in srgb, var(--muted) 45%, transparent)",
        color: "var(--muted)",
      }}
    >
      HT
    </span>
  );
}

const MONO: React.CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace",
  fontVariantNumeric: "tabular-nums",
};

function fmtDays(n: number): string {
  return n.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
}

/** One labelled figure in the client-filter banner. */
function ClientStat({ label, value, tag }: { label: string; value: string; tag?: "HT" | "TTC" }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ font: "600 10px/13px Sora", letterSpacing: ".05em", textTransform: "uppercase", color: "var(--muted2)" }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
        <span style={{ ...MONO, fontWeight: 600, fontSize: 16 }}>{value}</span>
        {tag && <BasisPill basis={tag} />}
      </div>
    </div>
  );
}

function pctLabel(part: number, whole: number): string {
  const v = whole > 0 ? (part / whole) * 100 : 0;
  return `${v.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

/**
 * Splits the TTC total into Net | URSSAF | TVA.
 *
 * Never conveys meaning by colour alone: each segment is also carried by a
 * labelled legend row, and the bar itself is a single role="img" whose
 * aria-label names all three parts with values and percents.
 */
function WhereItWentBar({
  t,
  ttcCents,
  netCents,
  urssafCents,
  vatCents,
}: {
  t: TFunction;
  ttcCents: number;
  netCents: number;
  urssafCents: number;
  vatCents: number;
}) {
  if (ttcCents <= 0) return null;
  const pct = (n: number) => `${((n / ttcCents) * 100).toFixed(1)}%`;

  const aria = t("reports.barAria", {
    ttc: formatEuros(ttcCents),
    net: formatEuros(netCents),
    netPct: pctLabel(netCents, ttcCents),
    urssaf: formatEuros(urssafCents),
    urssafPct: pctLabel(urssafCents, ttcCents),
    vat: formatEuros(vatCents),
    vatPct: pctLabel(vatCents, ttcCents),
  });

  const legend = [
    {
      key: "net",
      swatch: "linear-gradient(180deg, var(--primary-bright, #FF9A4D), var(--primary))",
      border: "none",
      title: t("reports.net"),
      titleColor: "var(--primary-deep, #D85F0E)",
      amount: netCents,
      caption: t("reports.netCaption"),
    },
    {
      key: "urssaf",
      swatch:
        "repeating-linear-gradient(45deg, var(--muted2) 0 3px, color-mix(in srgb, #fff 26%, var(--muted2)) 3px 6px)",
      border: "none",
      title: "URSSAF",
      titleColor: "var(--text)",
      amount: urssafCents,
      caption: t("reports.urssafCaption"),
    },
    {
      key: "tva",
      swatch:
        "repeating-linear-gradient(-45deg, transparent 0 3px, color-mix(in srgb, var(--muted2) 55%, transparent) 3px 4px)",
      border: "1px solid var(--muted2)",
      title: "TVA",
      titleColor: "var(--text)",
      amount: vatCents,
      caption: t("reports.vatCaptionPlain"),
    },
  ];

  return (
    <div style={{ padding: "4px 20px 18px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 9,
        }}
      >
        <span style={{ font: "600 11px/1 Sora", color: "var(--muted)" }}>
          {t("reports.whereItGoes", { amount: formatEuros(ttcCents) })}
        </span>
        <span style={{ fontSize: 11, color: "var(--muted2)" }}>{t("reports.ttcEquals")}</span>
      </div>

      <div
        role="img"
        aria-label={aria}
        style={{
          display: "flex",
          width: "100%",
          height: 34,
          borderRadius: 9,
          overflow: "hidden",
          border: "1px solid var(--border)",
          background: "var(--surface)",
        }}
      >
        <div
          style={{
            width: pct(netCents),
            minWidth: 44,
            background: "linear-gradient(180deg, var(--primary-bright, #FF9A4D), var(--primary))",
            display: "flex",
            alignItems: "center",
            padding: "0 10px",
          }}
        >
          <span
            style={{
              font: "700 11px Sora",
              color: "#fff",
              whiteSpace: "nowrap",
              textShadow: "0 1px 2px rgba(120,50,0,.35)",
            }}
          >
            {t("reports.net")} {pctLabel(netCents, ttcCents)}
          </span>
        </div>
        <div
          aria-hidden="true"
          style={{
            width: pct(urssafCents),
            minWidth: 30,
            borderLeft: "2px solid var(--card)",
            background:
              "repeating-linear-gradient(45deg, var(--muted2) 0 6px, color-mix(in srgb, #fff 26%, var(--muted2)) 6px 12px)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            width: pct(vatCents),
            minWidth: 30,
            borderLeft: "2px solid var(--card)",
            background:
              "repeating-linear-gradient(-45deg, transparent 0 5px, color-mix(in srgb, var(--muted2) 50%, transparent) 5px 6px)",
          }}
        />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 18px", marginTop: 12 }}>
        {legend.map((l) => (
          <div key={l.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 180, flex: 1 }}>
            <span
              style={{
                width: 15,
                height: 15,
                borderRadius: 4,
                flex: "none",
                marginTop: 1,
                background: l.swatch,
                border: l.border,
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ font: "600 12.5px Sora" }}>
                <span style={{ color: l.titleColor }}>{l.title}</span> · {formatEuros(l.amount)}{" "}
                <span style={{ color: "var(--muted2)", fontWeight: 500 }}>· {pctLabel(l.amount, ttcCents)}</span>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--muted)" }}>{l.caption}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The hero number, and the single highest-risk element on this screen.
 *
 * "Net" here means HT − URSSAF and nothing else. If it is read as take-home,
 * the user overspends by their entire income tax — a larger real-world error
 * than anything in the tax estimate. The basis is therefore carried in the
 * label and the body copy, not in a tooltip.
 */
function NetHero({ t, netCents }: { t: TFunction; netCents: number }) {
  return (
    <div
      style={{
        flex: 2,
        minWidth: 250,
        background: "var(--card)",
        border: "2px solid color-mix(in srgb, var(--primary) 42%, var(--border))",
        borderRadius: 15,
        boxShadow: "0 6px 22px var(--glow, rgba(244,122,32,.45))",
        padding: "16px 18px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          right: -30,
          top: -30,
          width: 130,
          height: 130,
          borderRadius: "50%",
          background: "radial-gradient(circle, var(--glow, rgba(244,122,32,.45)), transparent 70%)",
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            font: "700 10px/1 Sora",
            letterSpacing: ".08em",
            padding: "4px 7px",
            borderRadius: 6,
            background: "var(--primary)",
            color: "#fff",
          }}
        >
          NET
        </span>
        <span style={{ font: "700 13px Sora", letterSpacing: "-.01em", color: "var(--text)" }}>
          {t("reports.netFormula")}
        </span>
      </div>
      <div
        style={{
          ...MONO,
          fontWeight: 600,
          fontSize: 40,
          lineHeight: 1.05,
          letterSpacing: "-.01em",
          color: "var(--primary-deep, #D85F0E)",
          marginTop: 8,
          position: "relative",
        }}
      >
        {formatEuros(netCents)}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 6, position: "relative", maxWidth: 330 }}>
        {t("reports.netBodyPlain")}
      </div>
    </div>
  );
}

function EuroIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--muted2)"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: "none" }}
      aria-hidden="true"
    >
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

/**
 * After-tax estimate. Deliberately quieter than Net and never authoritative:
 * a real taux personnalise is exact for the foyer, but our simulated rate is
 * an annual, progressive figure applied to a slice — an approximation either
 * way, and always labelled as one.
 */
function AfterTaxLine({
  t,
  afterTax,
}: {
  t: TFunction;
  afterTax: NonNullable<ReportRangeData["totals"]["afterTax"]>;
}) {
  const tauxLabel = `${afterTax.tauxPct.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`;
  return (
    <div
      style={{
        marginTop: 14,
        border: "1px dashed color-mix(in srgb, var(--primary) 34%, var(--border))",
        borderRadius: 12,
        padding: "12px 15px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "6px 12px",
        background: "var(--surface)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9, flex: 1, minWidth: 200 }}>
        <EuroIcon />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
            {t("reports.afterTax")}{" "}
            <span style={{ color: "var(--muted2)" }}>
              ·{" "}
              {afterTax.source === "custom"
                ? t("reports.tauxPerso", { taux: tauxLabel })
                : t("reports.tauxEstimated", { taux: tauxLabel, year: afterTax.baremeYearUsed })}
            </span>
          </div>
          <div style={{ ...MONO, fontWeight: 600, fontSize: 19, color: "var(--text)", marginTop: 1 }}>
            ≈ {formatEuros(afterTax.amountCents)}
            <span style={{ font: "500 11px Sora", color: "var(--muted2)", marginLeft: 7 }}>
              {t("reports.onInvoiced")}
            </span>
          </div>
          {afterTax.isEstimateFromPriorYear && (
            <div style={{ fontSize: 11, color: "var(--muted2)", marginTop: 3 }}>
              {t("reports.priorYearScale", {
                next: (afterTax.baremeYearUsed ?? 0) + 1,
                used: afterTax.baremeYearUsed,
              })}
            </div>
          )}
          {afterTax.isProjected && (
            <div style={{ fontSize: 11, color: "var(--muted2)", marginTop: 3 }}>
              {t("reports.projected", { annual: formatEuros(afterTax.annualBasisHtCents) })}
            </div>
          )}
        </div>
      </div>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          font: "600 10px/1 Sora",
          letterSpacing: ".04em",
          textTransform: "uppercase",
          color: "var(--muted2)",
          background: "var(--card)",
          border: "1px solid var(--border)",
          padding: "5px 8px",
          borderRadius: 7,
        }}
      >
        {t("reports.estimateBadge")}
      </span>
    </div>
  );
}

/** Shown when no taux is set: present, never nagging. */
function AfterTaxPrompt({ t }: { t: TFunction }) {
  return (
    <div
      style={{
        marginTop: 14,
        border: "1px dashed var(--border)",
        borderRadius: 12,
        padding: "12px 15px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "8px 12px",
      }}
    >
      <EuroIcon />
      <div style={{ flex: 1, minWidth: 180, fontSize: 12.5, color: "var(--muted)" }}>
        {t("reports.tauxPromptPlain")}
      </div>
      <a
        href="/settings"
        style={{
          height: 34,
          padding: "0 13px",
          border: "1px solid var(--border)",
          borderRadius: 9,
          background: "var(--card)",
          color: "var(--primary-deep, #D85F0E)",
          font: "600 12px Sora",
          cursor: "pointer",
          whiteSpace: "nowrap",
          display: "inline-flex",
          alignItems: "center",
          textDecoration: "none",
        }}
      >
        {t("reports.addInSettings")}
      </a>
    </div>
  );
}

interface PreviewDay {
  date: string;
  status: string;
  billable: string;
  amount: string;
}

/**
 * Shown instead of the summary and preview when the range holds no days.
 *
 * Replaces them rather than sitting alongside: a summary card full of dashes
 * and a zeroed "where it went" bar invites the reader to interpret a layout
 * that has nothing to say.
 */
function EmptyState({ t }: { t: TFunction }) {
  return (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 16,
        boxShadow: "0 1px 2px rgba(15,26,48,.04),0 6px 18px rgba(15,26,48,.04)",
        padding: "44px 26px",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 58,
          height: 58,
          borderRadius: 17,
          background: "var(--surface)",
          border: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--muted2)",
        }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 3v1.5M12 19.5V21M5 5l1 1M18 18l1 1M3 12h1.5M19.5 12H21M5 19l1-1M18 6l1-1" />
        </svg>
      </div>
      <div style={{ font: "700 18px Sora", letterSpacing: "-.01em" }}>{t("reports.emptyTitle")}</div>
      <div style={{ fontSize: 13.5, color: "var(--muted)", maxWidth: 340 }}>
        {t("reports.emptyBody")}
      </div>
      <button
        disabled
        style={{
          marginTop: 6,
          height: 44,
          padding: "0 18px",
          border: "1px solid var(--border)",
          borderRadius: 10,
          background: "var(--surface)",
          color: "var(--muted2)",
          font: "600 13px Sora",
          cursor: "not-allowed",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3v12M8 11l4 4 4-4M5 21h14" />
        </svg>
        {t("reports.nothingToExport")}
      </button>
    </div>
  );
}

/**
 * One month in the grouped preview, expandable to its days.
 *
 * A real <button aria-expanded> rather than a click-handler div, so the
 * drill-down is reachable and announced by keyboard and screen reader.
 */
function MonthGroupRow({ t, month, days }: { t: TFunction; month: MonthRollup; days: PreviewDay[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: "1px solid var(--border)" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t(open ? "reports.collapse" : "reports.expand", {
          month: month.month,
          days: month.billableDays,
          amount: formatEuros(month.expectedCents),
        })}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 11,
          padding: "12px 16px",
          background: open ? "var(--surface)" : "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          color: "var(--text)",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            flex: "none",
            color: "var(--muted2)",
            display: "flex",
            transition: "transform .2s",
            transform: `rotate(${open ? 90 : 0}deg)`,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </span>
        <span style={{ ...MONO, fontWeight: 600, fontSize: 13, minWidth: 66 }}>{month.month}</span>
        <span style={{ fontSize: 12.5, color: "var(--muted)", minWidth: 42 }}>
          {month.billableDays} {t("reports.daysShort")}
        </span>
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ ...MONO, fontSize: 13 }}>{formatEuros(month.expectedHtCents)}</span>
            <BasisPill basis="HT" />
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ ...MONO, fontSize: 13, fontWeight: 600 }}>{formatEuros(month.expectedCents)}</span>
            <BasisPill basis="TTC" />
          </span>
        </span>
      </button>

      {open && (
        <div style={{ background: "var(--surface)", animation: "rpFade .18s ease both" }}>
          <div
            style={{
              display: "flex",
              padding: "7px 16px 7px 43px",
              font: "600 9px Sora",
              letterSpacing: ".06em",
              textTransform: "uppercase",
              color: "var(--muted2)",
              borderTop: "1px solid var(--border)",
            }}
          >
            <span style={{ flex: 1 }}>{t("reports.date")}</span>
            <span style={{ width: 90 }}>{t("reports.status")}</span>
            <span style={{ width: 52, textAlign: "right" }}>{t("reports.bill")}</span>
            <span style={{ width: 110, textAlign: "right" }}>HT</span>
          </div>
          {days.map((d) => (
            <div
              key={d.date}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "8px 16px 8px 43px",
                borderTop: "1px solid var(--border)",
                fontSize: 12.5,
              }}
            >
              <span style={{ flex: 1, ...MONO }}>{d.date}</span>
              <span style={{ width: 90, color: "var(--muted)" }}>{d.status}</span>
              <span style={{ width: 52, textAlign: "right", ...MONO }}>{d.billable}</span>
              <span
                style={{
                  width: 110,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 5,
                }}
              >
                <span style={MONO}>{d.amount}</span>
                <BasisPill basis="HT" />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 13,
  padding: "13px 15px",
};

function MicroLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        font: "600 10px/14px Sora",
        letterSpacing: ".06em",
        textTransform: "uppercase",
        color: "var(--muted2)",
      }}
    >
      {children}
    </div>
  );
}

/** Section divider inside the summary card ("Activity", "Money"). */
function BandLabel({ label, trailing, chip }: { label: string; trailing?: string; chip?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <span
        style={{
          font: "700 10px/1 Sora",
          letterSpacing: ".11em",
          textTransform: "uppercase",
          color: "var(--muted2)",
        }}
      >
        {label}
      </span>
      {chip && (
        <span style={{ font: "600 9px Sora", letterSpacing: ".05em", textTransform: "uppercase", background: "var(--surface)", border: "1px solid var(--border)", color: "var(--muted2)", padding: "3px 8px", borderRadius: 6, whiteSpace: "nowrap" }}>
          {chip}
        </span>
      )}
      <span aria-hidden="true" style={{ flex: 1, height: 1, background: "var(--border)" }} />
      {trailing && <span style={{ fontSize: 11.5, color: "var(--muted2)" }}>{trailing}</span>}
    </div>
  );
}

function MoneyTile({ label, cents, color }: { label: string; cents: number; color: string }) {
  return (
    <div
      style={{
        flex: 1,
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 13,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <MicroLabel>{label}</MicroLabel>
        <BasisPill basis="TTC" />
      </div>
      <div style={{ ...MONO, fontWeight: 600, fontSize: 20, marginTop: 5, color }}>{formatEuros(cents)}</div>
    </div>
  );
}

/**
 * Received, followed all the way down to what is actually yours.
 *
 * The headline stays TTC because that is what landed in the bank, but a TTC
 * figure alone is the thing that misleads: it contains the State's VAT, then
 * URSSAF, then income tax. Each step is shown with what it removed, so the
 * gap between "8 800 arrived" and "5 133 is mine" is legible rather than a
 * surprise at declaration time.
 */
function ReceivedTile({
  t,
  ttcCents,
  htCents,
  urssafCents,
  netCents,
  afterTax,
}: {
  t: TFunction;
  ttcCents: number;
  htCents: number;
  urssafCents: number;
  netCents: number;
  afterTax: ReportRangeData["totals"]["receivedAfterTax"];
}) {
  const vatCents = ttcCents - htCents;
  const step = (label: string, value: number, note?: string, accent?: string) => (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
      <span style={{ fontSize: 11.5, color: "var(--muted)" }}>
        {label}
        {note && <span style={{ color: "var(--muted2)" }}> {note}</span>}
      </span>
      <span style={{ ...MONO, fontSize: 12.5, fontWeight: 600, color: accent ?? "var(--text)" }}>
        {formatEuros(value)}
      </span>
    </div>
  );

  return (
    <div
      style={{
        flex: 1,
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 13,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <MicroLabel>{t("reports.received")}</MicroLabel>
        <BasisPill basis="TTC" />
      </div>
      <div style={{ ...MONO, fontWeight: 600, fontSize: 20, marginTop: 5, color: "var(--success, #2F8A5C)" }}>
        {formatEuros(ttcCents)}
      </div>

      {ttcCents > 0 && (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px dashed var(--border)",
            display: "flex",
            flexDirection: "column",
            gap: 5,
          }}
        >
          {step(t("reports.lessVat"), -vatCents, t("reports.notYours"))}
          {step(t("reports.lessUrssaf"), -urssafCents)}
          {step(t("reports.netOfBoth"), netCents, undefined, "var(--primary-deep, #D85F0E)")}
          {afterTax && (
            <>
              {step(t("reports.lessTax"), netCents - afterTax.amountCents)}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 8,
                  marginTop: 2,
                  paddingTop: 6,
                  borderTop: "1px solid var(--border)",
                }}
              >
                <span style={{ font: "700 11.5px Sora", color: "var(--text)" }}>
                  {t("reports.yoursEstimate")}
                </span>
                <span style={{ ...MONO, fontSize: 15, fontWeight: 600, color: "var(--text)" }}>
                  ≈ {formatEuros(afterTax.amountCents)}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Reports screen ───────────────────────────────────── */

export function Reports() {
  const { t } = useTranslation();
  const { data: settings } = useSettings();
  const today = parisToday();
  const month = currentParisMonth();
  const year = today.slice(0, 4);

  const [rangePreset, setRangePreset] = useState<RangePreset>("thismonth");
  const [customFrom, setCustomFrom] = useState(today);
  const [customTo, setCustomTo] = useState(today);
  /* The preview follows the design: worked days only. Payments still fund the
     totals (received, owed) and ride along in the exported file, so the range
     query and the export URL stay on "both". */
  const include: IncludeMode = "both";
  const [format, setFormat] = useState<"csv" | "xlsx">("csv");
  const [clientId, setClientId] = useState("");
  const { data: clientsData } = useClients();

  /* Resolve active range from preset */
  const { from, to } = ((): { from: string; to: string } => {
    if (rangePreset === "thismonth") {
      return { from: `${month}-01`, to: `${month}-31` };
    } else if (rangePreset === "ytd") {
      return { from: `${year}-01-01`, to: today };
    } else if (rangePreset === "trailing12") {
      return { from: trailing12Start(today), to: today };
    } else {
      return { from: customFrom, to: customTo };
    }
  })();

  const { data: rangeData, isLoading } = useReportRange(from, to, include, clientId || undefined);
  const clientActivity = rangeData?.clientActivity ?? null;
  const selectedClient = clientId ? (clientsData?.clients.find((c) => c.id === clientId) ?? null) : null;
  const clientName = selectedClient?.name ?? "";

  const workedRows = (rangeData?.rows ?? []).map((r) => ({
    date: r.date,
    status: r.status.charAt(0).toUpperCase() + r.status.slice(1),
    billable: r.billable > 0 ? String(r.billable) : "—",
    amount: r.billable > 0 ? formatEuros(r.expectedCents) : "—",
  }));


  const totals: ReportRangeData["totals"] = rangeData?.totals ?? {
    billableDays: 0,
    expectedHtCents: 0,
    vatCents: 0,
    expectedCents: 0,
    receivedCents: 0,
    owedCents: 0,
    urssafCents: 0,
    netCents: 0,
    afterTax: null,
    receivedHtCents: 0,
    receivedUrssafCents: 0,
    receivedNetCents: 0,
    receivedAfterTax: null,
  };

  /* Export URL */
  const exportUrl = `/api/reports/export.${format}?from=${from}&to=${to}&include=${include}`;
  const rangeReady = Boolean(from && to);

  /* Period label */
  const periodLabel =
    rangePreset === "thismonth"
      ? month
      : rangePreset === "ytd"
        ? `${year} YTD`
        : rangePreset === "trailing12"
          ? `Trailing 12`
          : from && to
            ? `${from} → ${to}`
            : "—";


  /* Preview shape follows the range span, not a user toggle. The export stays
     per-day either way — the preview is a view, the export is a record. */
  const months = rangeData?.months ?? [];
  const multiMonth = months.length > 1;
  const isEmpty = !isLoading && totals.billableDays === 0 && workedRows.length === 0;
  const previewCountLabel = multiMonth
    ? `${months.length} ${t("reports.months")}`
    : `${workedRows.length} ${workedRows.length === 1 ? t("reports.day") : t("reports.days")}`;
  const daysByMonth = new Map<string, PreviewDay[]>();
  for (const r of workedRows) {
    const key = r.date.slice(0, 7);
    const list = daysByMonth.get(key) ?? [];
    list.push(r);
    daysByMonth.set(key, list);
  }

  return (
    <div>
      {/* ── Controls card ────────────────────────────────── */}
      <div
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          boxShadow: "0 1px 2px rgba(15,26,48,.04),0 6px 18px rgba(15,26,48,.04)",
          padding: 18,
          marginBottom: 14,
        }}
      >
        {/* Range selector */}
        <SectionLabel>{t("reports.range")}</SectionLabel>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
          <PillBtn active={rangePreset === "thismonth"} onClick={() => setRangePreset("thismonth")}>
            This month
          </PillBtn>
          <PillBtn active={rangePreset === "ytd"} onClick={() => setRangePreset("ytd")}>
            Year to date
          </PillBtn>
          <PillBtn active={rangePreset === "trailing12"} onClick={() => setRangePreset("trailing12")}>
            Trailing 12
          </PillBtn>
          <PillBtn active={rangePreset === "custom"} onClick={() => setRangePreset("custom")}>
            Custom…
          </PillBtn>
        </div>

        {/* Custom date inputs */}
        {rangePreset === "custom" && (
          <>
            <SectionLabel>Date Range</SectionLabel>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 18 }}>
              <input
                aria-label="From date"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={inputStyle}
              />
              <span style={{ color: "var(--muted2)", fontSize: 13 }}>to</span>
              <input
                aria-label="To date"
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={inputStyle}
              />
            </div>
          </>
        )}

        {/* Format selector */}
        <SectionLabel>{t("reports.format")}</SectionLabel>
        <div style={{ display: "flex", gap: 8 }}>
          <PillBtn active={format === "csv"} onClick={() => setFormat("csv")}>
            CSV
          </PillBtn>
          <PillBtn active={format === "xlsx"} onClick={() => setFormat("xlsx")}>
            Excel .xlsx
          </PillBtn>
        </div>

        {/* Client filter — the shared chip row (hidden at a single client) */}
        <ClientFilter value={clientId} onChange={setClientId} />
      </div>

      {/* ── Client filter banner: scoped activity, person-level tax note ── */}
      {clientId && clientActivity && (
        <div
          style={{
            background: "color-mix(in srgb, var(--primary) 6%, var(--card))",
            border: "1px solid color-mix(in srgb, var(--primary) 25%, var(--border))",
            borderRadius: 14,
            padding: "14px 16px",
            marginBottom: 14,
          }}
        >
          <div style={{ font: "700 14px Sora", marginBottom: 8 }}>
            {t("reports.clientView", { name: clientName })}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
            <ClientStat label={t("reports.billable")} value={`${fmtDays(clientActivity.billableDays)} ${t("reports.daysShort")}`} />
            <ClientStat label={t("reports.expectedInvoiced")} value={formatEuros(clientActivity.expectedHtCents)} tag="HT" />
            <ClientStat label={t("reports.received")} value={formatEuros(clientActivity.receivedCents)} tag="TTC" />
            <ClientStat label={t("reports.owed")} value={formatEuros(clientActivity.owedCents)} tag="TTC" />
          </div>
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 10 }}>
            {t("reports.clientTaxNote")}
          </div>
        </div>
      )}

      {/* ── Empty state: replaces summary + preview entirely ── */}
      {!isLoading && isEmpty && <EmptyState t={t} />}

      {/* ── Summary narrative: Activity → where it went → Money ── */}
      {!isEmpty && (
      <div
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 18,
          boxShadow: "0 1px 2px rgba(15,26,48,.04),0 8px 22px rgba(15,26,48,.05)",
          overflow: "hidden",
          marginBottom: 14,
        }}
      >
        {/* band: ACTIVITY */}
        <div style={{ padding: "18px 20px 16px" }}>
          <BandLabel label={t("reports.activity")} trailing={periodLabel} chip={clientId ? t("clientFilter.all") : undefined} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
            <div style={{ flex: 1, minWidth: 130, ...panelStyle }}>
              <MicroLabel>{t("reports.billable")}</MicroLabel>
              <div style={{ ...MONO, fontWeight: 600, fontSize: 24, marginTop: 5 }}>{totals.billableDays}</div>
              <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 2 }}>{t("reports.days")}</div>
            </div>
            <div style={{ flex: 1.6, minWidth: 180, ...panelStyle }}>
              <MicroLabel>{t("reports.expectedInvoiced")}</MicroLabel>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5 }}>
                <span style={{ ...MONO, fontWeight: 600, fontSize: 24 }}>{formatEuros(totals.expectedCents)}</span>
                <BasisPill basis="TTC" />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 5 }}>
                <span style={{ ...MONO, fontSize: 13, color: "var(--muted)" }}>
                  {formatEuros(totals.expectedHtCents)}
                </span>
                <BasisPill basis="HT" />
                <span style={{ fontSize: 11, color: "var(--muted2)" }}>{t("reports.htBase")}</span>
              </div>
            </div>
          </div>
        </div>

        <WhereItWentBar
          t={t}
          ttcCents={totals.expectedCents}
          netCents={totals.netCents}
          urssafCents={totals.urssafCents}
          vatCents={totals.vatCents}
        />

        {/* band: MONEY */}
        <div
          style={{
            padding: "18px 20px 20px",
            borderTop: "1px solid var(--border)",
            background: "color-mix(in srgb, var(--primary) 4%, var(--card))",
          }}
        >
          <BandLabel label={t("reports.money")} chip={clientId ? t("clientFilter.all") : undefined} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "stretch" }}>
            <NetHero t={t} netCents={totals.netCents} />
            <div style={{ flex: 1, minWidth: 150, display: "flex", flexDirection: "column", gap: 12 }}>
              <ReceivedTile
                t={t}
                ttcCents={totals.receivedCents}
                htCents={totals.receivedHtCents}
                urssafCents={totals.receivedUrssafCents}
                netCents={totals.receivedNetCents}
                afterTax={totals.receivedAfterTax}
              />
              <MoneyTile
                label={t("reports.owed")}
                cents={totals.owedCents}
                color={totals.owedCents > 0 ? "var(--danger, #C8472E)" : "var(--success, #2F8A5C)"}
              />
            </div>
          </div>
          {totals.afterTax ? (
            <AfterTaxLine t={t} afterTax={totals.afterTax} />
          ) : (
            <AfterTaxPrompt t={t} />
          )}
        </div>
      </div>
      )}

      {/* ── Preview table card ───────────────────────────── */}
      {!isEmpty && (
      <div
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          boxShadow: "0 1px 2px rgba(15,26,48,.04),0 6px 18px rgba(15,26,48,.04)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--border)",
            font: "600 13px Sora",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {t("reports.exportPreview")}{" "}
          <span style={{ fontSize: 11, color: "var(--muted2)", fontWeight: 500 }}>
            {periodLabel} · {previewCountLabel}
          </span>
        </div>

        {/* Worked days — grouped by month when the range spans more than one */}
        {multiMonth && (
          <div style={{ maxHeight: 290, overflowY: "auto" }}>
            {months.map((m) => (
              <MonthGroupRow key={m.month} t={t} month={m} days={daysByMonth.get(m.month) ?? []} />
            ))}
          </div>
        )}

        {/* Worked days table — single month, days directly */}
        {!multiMonth && (
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ position: "sticky", top: 0, background: "var(--surface)" }}>
                  {(
                    [
                      ["Date", "left", "9px 18px"],
                      ["Status", "left", "9px 8px"],
                      ["Bill.", "right", "9px 8px"],
                      ["Amount", "right", "9px 18px"],
                    ] as const
                  ).map(([label, align, pad]) => (
                    <th
                      key={label}
                      style={{
                        textAlign: align,
                        padding: pad,
                        font: "600 10px Sora",
                        letterSpacing: ".06em",
                        textTransform: "uppercase",
                        color: "var(--muted2)",
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={4} style={{ padding: "18px", textAlign: "center", color: "var(--muted2)", fontSize: 13 }}>
                      Loading…
                    </td>
                  </tr>
                )}
                {workedRows.map((r) => (
                  <tr key={r.date} data-testid="preview-row" style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "9px 18px", fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums" }}>
                      {r.date}
                    </td>
                    <td style={{ padding: "9px 8px", color: "var(--muted)" }}>{r.status}</td>
                    <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums" }}>
                      {r.billable}
                    </td>
                    <td style={{ padding: "9px 18px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums" }}>
                      {r.amount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}


        {/* Download button */}
        <div style={{ padding: "14px 18px", borderTop: "1px solid var(--border)" }}>
          <a
            href={exportUrl}
            download
            data-testid="download-link"
            aria-disabled={!rangeReady}
            style={{
              width: "100%",
              height: 46,
              border: "none",
              borderRadius: 10,
              background: "var(--primary)",
              color: "#fff",
              font: "600 14px Sora",
              cursor: "pointer",
              boxShadow: "0 3px 12px var(--glow)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              textDecoration: "none",
              pointerEvents: rangeReady ? "auto" : "none",
              opacity: rangeReady ? 1 : 0.45,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v12M8 11l4 4 4-4M5 21h14" />
            </svg>
            {t("reports.download", { format: format.toUpperCase() })}
          </a>

          {/* Relevé d'activité — the branded, money-first PDF for an accountant.
              Covers whatever range is selected: the totals and day list passed
              in are already the range's, so a single month or a trailing year
              both produce a coherent statement. */}
          <button
            onClick={() =>
              exportRelevePdf({
                identity: {
                  name: settings?.bizName ?? "",
                  siret: settings?.bizSiret ?? "",
                  address: settings?.bizAddress ?? "",
                },
                // Under a client filter this is an activity statement for that
                // client: HT and days for the client, TTC = HT (VAT is
                // person-level and not split here). Unfiltered, it is the full
                // person-level statement with real VAT and a per-client breakdown.
                monthLabel: periodLabel,
                generatedOn: new Intl.DateTimeFormat("fr-FR").format(new Date()),
                billableDays: clientActivity ? clientActivity.billableDays : totals.billableDays,
                htCents: clientActivity ? clientActivity.expectedHtCents : totals.expectedHtCents,
                vatCents: clientActivity ? 0 : totals.vatCents,
                ttcCents: clientActivity ? clientActivity.expectedHtCents : totals.expectedCents,
                receivedCents: clientActivity ? clientActivity.receivedCents : totals.receivedCents,
                owedCents: clientActivity ? clientActivity.owedCents : totals.owedCents,
                franchise: clientActivity ? true : totals.vatCents === 0,
                ...(clientActivity && selectedClient
                  ? { client: { name: selectedClient.name, siret: selectedClient.siret, address: selectedClient.address } }
                  : {}),
                ...(!clientId && rangeData?.clientBreakdown && rangeData.clientBreakdown.length > 1
                  ? { breakdown: rangeData.clientBreakdown.map((b) => ({ name: b.name, days: b.days, htCents: b.htCents })) }
                  : {}),
                days: (rangeData?.rows ?? []).map((r) => ({
                  date: r.date,
                  status: r.status.toLowerCase() as "full" | "half" | "none" | "sick" | "holiday",
                  billable: r.billable,
                })),
                invoices: (rangeData?.payments ?? [])
                  .filter((p) => p.invoiceRef)
                  .map((p) => ({ period: p.period, ref: p.invoiceRef as string, url: p.invoiceUrl })),
              })
            }
            disabled={!rangeReady || isEmpty}
            style={{
              width: "100%",
              height: 42,
              marginTop: 10,
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "var(--card)",
              color: "var(--primary-deep, #D85F0E)",
              font: "600 13px Sora",
              cursor: !rangeReady || isEmpty ? "not-allowed" : "pointer",
              opacity: !rangeReady || isEmpty ? 0.45 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6M9 15h6" />
            </svg>
            {t("reports.releveDownload")}
          </button>

          <div style={{ textAlign: "center", fontSize: 11, color: "var(--muted2)", marginTop: 9 }}>
            {t("reports.exportNotePlain")}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
